import { useCallback } from 'react';
import { getAccount, readContract, signTypedData, waitForTransactionReceipt } from '@wagmi/core';
import dotenv from 'dotenv';

import wagmiConfig from 'wagmiConfig';
import {
  ERC20ABI,
  ERC721ABI,
  ExchangeContractABI,
  ExchangeHelperContractABI,
} from 'constants/abis';
import {
  EXCHANGE_PROXY_ADDRESS,
  EXCHANGE_HELPER_PROXY_ADDRESS,
  EIP712_SIGNER_MESSAGE,
  MAX_APPROVAL_AMOUNT,
  DEFAULT_NETWORK,
  SOMETHING_WENT_WRONG,
  WRAPPED_PROXY_ADDRESS,
} from 'constants/index';
import { useSwitchNetwork } from './common';
import useWalletContext from 'hooks/useWalletContext';
import { calculateGas, useContract } from 'utils/index';

dotenv.config();

const chainId = parseInt(localStorage.storedChain) || parseInt(DEFAULT_NETWORK);
const exchangeProxy = EXCHANGE_PROXY_ADDRESS[chainId];
const exchangeHelperProxy = EXCHANGE_HELPER_PROXY_ADDRESS[chainId];

export const useSwitchNetworkAndReload = () => {
  const { chainId: currentChainId } = useWalletContext();
  const switchNetwork = useSwitchNetwork();

  const switchNetworkAndReload = useCallback(async () => {
    if (currentChainId === chainId) {
      return;
    }

    const networkCheck = await switchNetwork();
    if (!networkCheck) {
      throw Error('Network change request rejected');
    }

    window.location.reload();
  }, [currentChainId, switchNetwork]);
  return switchNetworkAndReload;
};

// check if exchange proxy is approved for given tokenId, if not get approval
export const useApproveExchange = ({ contractAddress, tokenId }) => {
  const nftContract = contractAddress && useContract(contractAddress, ERC721ABI.abi);

  const handleApproveExchange = useCallback(async () => {
    const gasEstimate = await nftContract.estimateGas.approve([exchangeProxy, tokenId]);
    const txHash = await nftContract.write.approve([exchangeProxy, tokenId], {
      gas: calculateGas(gasEstimate),
    });
    const receipt = await waitForTransactionReceipt(wagmiConfig, { hash: txHash });
    return receipt;
  }, [nftContract, tokenId]);
  return handleApproveExchange;
};

export const useCheckExchangeApproved = ({ contractAddress, tokenId }) => {
  const nftContract = contractAddress && useContract(contractAddress, ERC721ABI.abi);
  const switchNetworkAndReload = useSwitchNetworkAndReload();

  const checkExchangeApproved = useCallback(async () => {
    if (!contractAddress) {
      throw Error(SOMETHING_WENT_WRONG);
    }

    await switchNetworkAndReload();

    // check if exchange contract proxy is already approved
    const approvedAdr = await nftContract.read.getApproved([tokenId]);
    if (!approvedAdr) {
      return;
    }
    return approvedAdr.toLowerCase() === exchangeProxy.toLowerCase();
  }, [contractAddress, nftContract, switchNetworkAndReload, tokenId]);
  return checkExchangeApproved;
};

export const useApproveAllExchange = ({ contractAddress }) => {
  const nftContract = contractAddress && useContract(contractAddress, ERC721ABI.abi);

  const handleApproveAllExchange = useCallback(async () => {
    const gasEstimate = await nftContract.estimateGas.setApprovalForAll([exchangeProxy, true]);
    const txHash = await nftContract.write.setApprovalForAll([exchangeProxy, true], {
      gas: calculateGas(gasEstimate),
    });
    const receipt = await waitForTransactionReceipt(wagmiConfig, { hash: txHash });
    return receipt;
  }, [nftContract]);

  return handleApproveAllExchange;
};

export const useCheckExchangeApprovedAll = ({ contractAddress, ownerAddress }) => {
  const nftContract = contractAddress && useContract(contractAddress, ERC721ABI.abi);
  const switchNetworkAndReload = useSwitchNetworkAndReload();

  const checkExchangeApprovedAll = useCallback(async () => {
    if (!contractAddress || !ownerAddress) {
      throw Error(SOMETHING_WENT_WRONG);
    }

    await switchNetworkAndReload();

    // Check if exchange contract proxy is already approved for all NFTs of the owner
    const isApprovedForAll = await nftContract.read.isApprovedForAll([ownerAddress, exchangeProxy]);
    return isApprovedForAll;
  }, [contractAddress, nftContract, switchNetworkAndReload, ownerAddress]);

  return checkExchangeApprovedAll;
};

// check if exchange proxy is approved for given tokenId, if not get approval
export const useApproveExchangeWrapped = () => {
  const wrappedContract = useContract(WRAPPED_PROXY_ADDRESS[chainId], ERC20ABI.abi);

  const handleApproveExchangeWrapped = useCallback(async () => {
    const gasEstimate = await wrappedContract.estimateGas.approve([
      exchangeProxy,
      BigInt(MAX_APPROVAL_AMOUNT),
    ]);
    const tx = await wrappedContract.write.approve([exchangeProxy, BigInt(MAX_APPROVAL_AMOUNT)], {
      gas: calculateGas(gasEstimate),
    });
    return tx;
  }, [wrappedContract]);
  return handleApproveExchangeWrapped;
};

export const useGetExchangeApprovedWrapped = () => {
  const switchNetworkAndReload = useSwitchNetworkAndReload();
  const { address } = useWalletContext();

  const getExchangeApprovedWrapped = useCallback(async () => {
    await switchNetworkAndReload();

    // check if exchange contract proxy is already approved
    const approveAmount = await readContract(wagmiConfig, {
      abi: ERC20ABI.abi,
      address: WRAPPED_PROXY_ADDRESS[chainId],
      functionName: 'allowance',
      args: [address, exchangeProxy],
      account: address,
    });
    return approveAmount;
  }, [address, switchNetworkAndReload]);
  return getExchangeApprovedWrapped;
};

// check if order maker still hold the NFT
export const useCheckIfMakerIsOwner = ({ contractAddress }) => {
  const nftContract = contractAddress && useContract(contractAddress, ERC721ABI.abi);
  const checkIfMakerIsOwner = useCallback(
    async ({ contractAddress, maker, tokenId }) => {
      try {
        if (!contractAddress || !maker || !tokenId) {
          return false;
        }
        const owner = await nftContract.read.ownerOf([tokenId]);
        if (owner && owner.toLowerCase() === maker.toLowerCase()) {
          return true;
        }
      } catch (e) {
        console.error(e);
      }
      return false;
    },
    [nftContract],
  );

  return checkIfMakerIsOwner;
};

// Recursive function to check orderKeyHash of order and to check if any such order is already filled.
const checkAndUpdateOrderIfFilled = async (order, address) => {
  try {
    if (!order) {
      return false;
    }

    const orderKeyHash = await readContract(wagmiConfig, {
      abi: ExchangeHelperContractABI,
      address: exchangeHelperProxy,
      functionName: 'hashKey',
      args: [order],
      account: address,
    });
    const filled = await readContract(wagmiConfig, {
      abi: ExchangeContractABI,
      address: exchangeProxy,
      functionName: 'getOrderFill',
      args: [orderKeyHash],
      account: address,
    });

    if (filled == 0) {
      return order;
    } else {
      return checkAndUpdateOrderIfFilled(
        {
          ...order,
          salt: order.salt + 1,
        },
        address,
      );
    }
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const useCheckAndUpdateOrderIfFilled = () => {
  const { address } = useWalletContext();

  const check = async (order) => {
    return await checkAndUpdateOrderIfFilled(order, address);
  };

  return check;
};

export const useSignOrder = () => {
  const { connector } = getAccount(wagmiConfig);

  const sign = useCallback(async (message) => {
    const msgParams = {
      ...EIP712_SIGNER_MESSAGE,
      connector,
      domain: {
        name: 'Energi',
        version: '1',
        chainId,
        verifyingContract: EXCHANGE_PROXY_ADDRESS[chainId].toLowerCase(),
      },
      message,
    };
    try {
      const response = await signTypedData(wagmiConfig, msgParams);
      return response;
    } catch (err) {
      return console.error(err);
    }
  }, []);
  return sign;
};

export const useMultiSignOrder = () => {
  const { address } = useWalletContext();
  const { connector } = getAccount(wagmiConfig);
  const multiSign = useCallback(
    async (messages) => {
      const signPromises = messages.map(async (message) => {
        const msgParams = {
          ...EIP712_SIGNER_MESSAGE,
          connector,
          domain: {
            name: 'Energi',
            version: '1',
            chainId,
            verifyingContract: EXCHANGE_PROXY_ADDRESS[chainId].toLowerCase(),
          },
          message,
        };

        return signTypedData(wagmiConfig, msgParams).catch((err) => {
          console.error(err);
          throw new Error('Signature rejected');
        });
      });

      const signedOrders = await Promise.all(signPromises);
      return signedOrders;
    },
    [address],
  );

  return multiSign;
};

// BUY NFT
export const useMatchBuyOrderTx = () => {
  const { address } = useWalletContext();
  const exchangeContractProxy = exchangeProxy && useContract(exchangeProxy, ExchangeContractABI);

  const handleMatchBuyOrderTx = useCallback(
    async ({
      amount,
      makerOrder,
      takerOrder,
      makerSign,
      matchBeforeTimestamp,
      matchAllowanceSignature,
    }) => {
      if (!address) {
        return null;
      }
      try {
        const gasEstimate = await exchangeContractProxy.estimateGas.matchOrders(
          [
            takerOrder,
            '0x',
            0,
            '0x',
            makerOrder,
            makerSign,
            matchBeforeTimestamp,
            matchAllowanceSignature,
          ],
          { from: address, value: amount },
        );

        const hash = await exchangeContractProxy.write.matchOrders(
          [
            takerOrder, // orderLeft - buyer
            '0x', // signatureLeft
            0, // matchLeftBeforeTimestamp
            '0x', // orderBookSignatureLeft
            makerOrder, // orderRight - seller
            makerSign, //signatureRight
            matchBeforeTimestamp, // matchRightBeforeTimestamp
            matchAllowanceSignature, // orderBookSignatureRight
          ],
          {
            from: address,
            value: amount,
            gas: calculateGas(gasEstimate),
          },
        );

        return hash;
      } catch (e) {
        console.log(e);
        console.error(e);
        return null;
      }
    },
    [address, exchangeContractProxy],
  );

  return handleMatchBuyOrderTx;
};

export const useMatchAcceptOrderTx = () => {
  const { address } = useWalletContext();
  const exchangeContractProxy = exchangeProxy && useContract(exchangeProxy, ExchangeContractABI);

  const handleMatchAcceptOrder = useCallback(
    async ({
      makerOrder,
      takerOrder,
      makerSign,
      matchBeforeTimestamp,
      matchAllowanceSignature,
    }) => {
      if (!address) {
        return null;
      }
      try {
        const gasEstimate = await exchangeContractProxy.estimateGas.matchOrders(
          [
            takerOrder,
            '0x',
            0,
            '0x',
            makerOrder,
            makerSign,
            matchBeforeTimestamp,
            matchAllowanceSignature,
          ],
          { from: address },
        );
        const hash = await exchangeContractProxy.write.matchOrders(
          [
            takerOrder, // orderRight
            '0x', //signatureRight
            0, // matchRightBeforeTimestamp
            '0x', // orderBookSignatureRight
            makerOrder, // orderLeft
            makerSign, // signatureLeft
            matchBeforeTimestamp, // matchLeftBeforeTimestamp
            matchAllowanceSignature, // orderBookSignatureLeft
          ],
          {
            from: address,
            gas: calculateGas(gasEstimate),
          },
        );

        return hash;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    [address, exchangeContractProxy],
  );

  return handleMatchAcceptOrder;
};

// SWEEP NFTS
export const useBatchMatchBuyOrderTx = () => {
  const { address } = useWalletContext();
  const exchangeContractProxy = exchangeProxy && useContract(exchangeProxy, ExchangeContractABI);

  const handleBatchMatchBuyOrderTx = useCallback(
    async ({ amount, orders, makerSigns, matchBeforeTimestamps, matchAllowanceSignatures }) => {
      if (!address) {
        return null;
      }
      try {
        const gasEstimate = await exchangeContractProxy.estimateGas.batchMatchOrders(
          [orders, makerSigns, matchBeforeTimestamps, matchAllowanceSignatures],
          { value: amount },
        );
        const hash = await exchangeContractProxy.write.batchMatchOrders(
          [
            orders, // pairs of left and right orders
            makerSigns, // pairs of left and right signatures
            matchBeforeTimestamps, // pairs of left and right matchBeforeTimestamps
            matchAllowanceSignatures, // pairs of left and right orderBookSignatures
          ],
          {
            from: address,
            value: amount,
            gas: calculateGas(gasEstimate),
          },
        );

        return hash;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    [address, exchangeContractProxy],
  );

  return handleBatchMatchBuyOrderTx;
};

// ACCEPT BIDS
export const useMatchAcceptBidsOrderTx = () => {
  const { address } = useWalletContext();
  const exchangeHelperContractProxy = useContract(exchangeHelperProxy, ExchangeHelperContractABI);

  const handleMatchAcceptBidsOrder = useCallback(
    async (batchedBidOrders) => {
      if (!address) {
        return null;
      }
      try {
        const gasEstimate = await exchangeHelperContractProxy.estimateGas.matchCollectionBidOrders(
          [batchedBidOrders],
          { from: address, value: 0 },
        );
        const hash = await exchangeHelperContractProxy.write.matchCollectionBidOrders(
          [batchedBidOrders],
          {
            from: address,
            value: 0,
            gas: calculateGas(gasEstimate),
          },
        );

        return hash;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    [address, exchangeHelperContractProxy],
  );

  return handleMatchAcceptBidsOrder;
};

export const usePairBidKeys = () => {
  const { address } = useWalletContext();
  const exchangeHelperContractProxy = useContract(exchangeHelperProxy, ExchangeHelperContractABI);

  const handlePairBidKeys = useCallback(
    async (batchedBidOrders) => {
      if (!address) {
        return null;
      }
      try {
        const pairedBidKeys = await Promise.all(
          batchedBidOrders.map(async (bidOrderPerTx) => {
            const placedBidKey = await readContract(wagmiConfig, {
              abi: ExchangeHelperContractABI,
              address: exchangeHelperProxy,
              functionName: 'hashKey',
              args: [bidOrderPerTx[0][0]],
              account: address,
            });

            const nftKeyPromises = bidOrderPerTx[0].map(async (order, index) => {
              const nftKey = await readContract(wagmiConfig, {
                abi: ExchangeHelperContractABI,
                address: exchangeHelperProxy,
                functionName: 'hashKey',
                args: [order],
                account: address,
              });
              if (nftKey !== placedBidKey) {
                return {
                  placedBidKey,
                  nftKey,
                  tokenId: bidOrderPerTx[4][index],
                  contractAddress: bidOrderPerTx[5][index],
                };
              }
              return null;
            });

            const validPairs = (await Promise.all(nftKeyPromises)).filter(Boolean);

            return validPairs;
          }),
        );

        const flattenedPairedBidKeys = pairedBidKeys.flat();

        return flattenedPairedBidKeys;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    [address, exchangeHelperContractProxy],
  );

  return handlePairBidKeys;
};
